import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/client';
import { termsOfUse, type TermsCurrentVersion } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { useToast } from '@/shared/ui/useToast';

/** Chave da consulta do termo vigente, que a moldura e a tela de aceites compartilham. */
export const CHAVE_DO_TERMO_VIGENTE = ['terms-of-use', 'current'] as const;

/**
 * O termo de uso vigente com o botao "De acordo com o termo de uso" (CPO-021).
 *
 * Com o aceite obrigatorio, o dialogo nao fecha: a pessoa aceita ou encerra a sessao.
 * Sem ele, a pessoa le, aceita ou fecha e volta depois pelo aviso da moldura.
 *
 * O aceite leva a versao que a pessoa leu. Se outra entrou em vigor no meio, o
 * servidor recusa, e a tela carrega a versao nova para ela ler de novo.
 */
export function TermsOfUseModal({
  open,
  version,
  blocking,
  onClose,
}: {
  open: boolean;
  version: TermsCurrentVersion;
  blocking: boolean;
  onClose: () => void;
}) {
  const { signOut } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const aceitar = useMutation({
    mutationFn: () => termsOfUse.accept(version.id),
    onSuccess: () => {
      toast.success('Aceite do termo de uso registrado.');
      void queryClient.invalidateQueries({ queryKey: CHAVE_DO_TERMO_VIGENTE });
      onClose();
    },
    onError: (erro) => {
      if (erro instanceof ApiError && erro.code === 'TERMO_VERSAO_DESATUALIZADA') {
        void queryClient.invalidateQueries({ queryKey: CHAVE_DO_TERMO_VIGENTE });
      }
    },
  });

  const vigencia = version.effective_from
    ? `Versao ${version.version}, vigente desde ${formatDateTime(version.effective_from)}.`
    : `Versao ${version.version}.`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!blocking}
      size="lg"
      title={version.title || 'Termo de uso'}
      description={vigencia}
      footer={
        <>
          {blocking ? (
            <Button variant="secondary" onClick={() => void signOut()}>
              Encerrar sessao
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Ler depois
            </Button>
          )}
          <Button loading={aceitar.isPending} onClick={() => aceitar.mutate()}>
            De acordo com o termo de uso
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {blocking ? (
          <Alert tone="warning" title="Aceite obrigatorio">
            A empresa exige o aceite do termo de uso vigente antes de continuar.
          </Alert>
        ) : null}
        {aceitar.error ? (
          <Alert tone="danger" title="Nao foi possivel registrar o aceite">
            {aceitar.error.message}
          </Alert>
        ) : null}
        <RichTextView html={version.body} />
        <p className="text-xs text-ink-500">
          O aceite fica registrado com a versao, a data e a hora, e o endereco e o
          navegador de onde ele partiu.
        </p>
      </div>
    </Modal>
  );
}
